import { X, AlertCircle, Loader2 } from 'lucide-react';
import { cn, Button } from '@scio3/ui';
import { useTranslation } from 'react-i18next';
import type { UploadedImage } from '../hooks/useImageUpload';

interface ImagePreviewBarProps {
  images: UploadedImage[];
  onRemove: (localId: string) => void;
}

/**
 * Row of image thumbnails above the chat textarea.
 * Shows upload spinner or error overlay per image, with a remove (X) button on hover.
 */
export function ImagePreviewBar({ images, onRemove }: ImagePreviewBarProps) {
  const { t } = useTranslation();

  if (images.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 pb-2">
      {images.map((img) => (
        <div
          key={img.localId}
          data-testid="optibot-image-thumb"
          data-status={img.status}
          className="group/thumb relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border"
        >
          <img
            src={img.previewUrl}
            alt={img.file.name}
            className={cn(
              'h-full w-full object-cover',
              img.status === 'uploading' && 'opacity-50',
              img.status === 'error' && 'opacity-30',
            )}
          />
          {img.status === 'uploading' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          )}
          {img.status === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            className="absolute -right-1 -top-1 h-5 w-5 rounded-full bg-background/80 opacity-0 shadow-sm transition-opacity group-hover/thumb:opacity-100"
            onClick={() => onRemove(img.localId)}
            aria-label={t('optibot.image.remove')}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}
